/**
 * TrustBadge — iter178.
 *
 * Renders a small "Verified author" chip when the backend returns
 * `trust.level === "verified"` on a public share / embed response.
 * Hidden entirely when the badge isn't earned — never renders an empty pill.
 *
 * On hover / focus, expands a small popover explaining WHICH checks the
 * author passed, plus a "Learn more" link that opens the /trust marketing
 * page (see components/public/TrustExplainerPage.jsx).
 *
 * Two visual variants:
 *   `variant="pill"`  (default) — rounded green pill for share/analytics pages
 *   `variant="inline"`           — compact inline mark for embed widget footer
 *                                  (uses inline styles so it renders inside
 *                                  the styles-in-JS embed context).
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Users } from 'lucide-react';

const REASON_LABELS = {
  email_verified: 'Email verified via click-through link',
  human_signup: 'Passed the Cloudflare bot check on signup',
  no_disposable: 'Real, non-disposable email domain',
  team_member: 'Publishing under a team account',
  paying_team: 'Team is on an active paid subscription',
};

// iter179 — two-tier color scheme. Team beats Verified; both share the
// popover pattern but wear their own colors so readers can visually
// distinguish tiers at a glance.
const TIER_STYLES = {
  verified: {
    label: 'Verified author',
    tooltipTitle: 'Why this author is verified',
    pillBg: 'bg-emerald-500/15',
    pillBorder: 'border-emerald-500/40',
    pillText: 'text-emerald-300',
    pillHover: 'hover:bg-emerald-500/25',
    ring: 'focus-visible:ring-emerald-400',
    icon: ShieldCheck,
    inlineFg: '#10B981',
    inlineBg: 'rgba(16, 185, 129, 0.12)',
    inlineBorder: 'rgba(16, 185, 129, 0.35)',
    popoverBorder: 'border-emerald-500/30',
    checkColor: 'text-emerald-400',
    tooltipTitleColor: 'text-emerald-300',
    learnMore: 'text-emerald-400 hover:text-emerald-300',
  },
  team_verified: {
    label: 'Team-verified',
    tooltipTitle: 'Why this team is verified',
    pillBg: 'bg-amber-500/15',
    pillBorder: 'border-amber-500/50',
    pillText: 'text-amber-300',
    pillHover: 'hover:bg-amber-500/25',
    ring: 'focus-visible:ring-amber-400',
    icon: Users,
    inlineFg: '#F59E0B',
    inlineBg: 'rgba(245, 158, 11, 0.15)',
    inlineBorder: 'rgba(245, 158, 11, 0.45)',
    popoverBorder: 'border-amber-500/40',
    checkColor: 'text-amber-400',
    tooltipTitleColor: 'text-amber-300',
    learnMore: 'text-amber-400 hover:text-amber-300',
  },
};

const TrustBadge = ({ trust, variant = 'pill' }) => {
  const [expanded, setExpanded] = useState(false);
  if (!trust || !trust.level || !TIER_STYLES[trust.level]) return null;

  const style = TIER_STYLES[trust.level];
  const IconCmp = style.icon;
  const reasons = (trust.reasons || []).filter((r) => REASON_LABELS[r]);
  const teamName = trust.team && trust.team.name;

  // --- Inline (embed widget footer) variant -------------------------------
  if (variant === 'inline') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          fontSize: '11px',
          fontWeight: 600,
          color: style.inlineFg,
          padding: '1px 6px',
          background: style.inlineBg,
          border: `1px solid ${style.inlineBorder}`,
          borderRadius: '4px',
        }}
        title={`${style.label} — see /trust`}
        data-testid="trust-badge-inline"
        data-tier={trust.level}
      >
        <IconCmp style={{ width: '11px', height: '11px' }} />
        {trust.level === 'team_verified' ? 'Team' : 'Verified'}
      </span>
    );
  }

  // --- Pill (share / snapshot page) variant -------------------------------
  return (
    <span className="relative inline-block" data-testid="trust-badge" data-tier={trust.level}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
        onFocus={() => setExpanded(true)}
        onBlur={() => setExpanded(false)}
        aria-label={`${style.label} — click to see why`}
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ${style.pillBg} border ${style.pillBorder} ${style.pillText} text-xs font-semibold ${style.pillHover} transition-colors focus:outline-none focus-visible:ring-2 ${style.ring}`}
        data-testid="trust-badge-pill"
      >
        <IconCmp className="h-3 w-3" />
        {style.label}
      </button>
      {expanded && (
        <div
          className={`absolute left-0 top-full mt-2 w-72 z-50 rounded-lg border ${style.popoverBorder} bg-[#0F1730] shadow-xl p-3 text-left`}
          role="tooltip"
          data-testid="trust-badge-popover"
        >
          <p className={`text-xs ${style.tooltipTitleColor} font-semibold mb-1 flex items-center gap-1.5`}>
            <IconCmp className="h-3.5 w-3.5" />
            {style.tooltipTitle}
          </p>
          {teamName && (
            <p className="text-[11px] text-slate-300 mb-2 pb-2 border-b border-white/5">
              Publishing under <strong className="text-white">{teamName}</strong>
            </p>
          )}
          <ul className="text-[11px] text-slate-300 space-y-1 mb-2">
            {reasons.map((r) => (
              <li key={r} className="flex items-start gap-1.5">
                <span className={`${style.checkColor} mt-0.5`}>✓</span>
                <span>{REASON_LABELS[r]}</span>
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-slate-500 leading-relaxed">
            &ldquo;Verified&rdquo; means a real human owns this account — it does <em>not</em>{' '}
            certify the content is factually true.
          </p>
          <Link
            to="/trust"
            className={`text-[10px] ${style.learnMore} hover:underline mt-1 inline-block`}
            data-testid="trust-badge-learn-more"
          >
            Learn more →
          </Link>
        </div>
      )}
    </span>
  );
};

export default TrustBadge;
